import { Card, Typography } from '@material-ui/core'
import { useFormikContext } from 'formik'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import InputField from '../../components/Form/InputField'
import { FieldT } from '../../components/Form/types'
import ResponsiveGrid from '../../components/ResponsiveGrid'
import { media } from '../../theme'
import { Skeleton } from '@material-ui/lab'
import Hero from '../../components/Hero'

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`

const HeroContainer = styled(FlexContainer)`
  padding: 16px 0;
  position: relative;
  align-items: flex-start;

  ${media.small`
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & :nth-child(1) { order: 1}
    & :nth-child(2) { order: 3}
    & :nth-child(3) { order: 2}
  `}
`
//`

const HeroDetailsCard = styled(Card).attrs({ variant: 'outlined' })`
  flex-grow: 1;
  padding: 16px;
  margin-left: 16px;

  ${media.small`
    margin-left: 0px;
    margin-top: 8px;
  `}
`

const HeroFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  margin-bottom: 16px;

  ${media.medium`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.small`
    grid-template-columns: auto;
  `}
`

const DetailsCard = styled(Card).attrs({ variant: 'outlined' })`
  width: 100%;
  padding: 16px;
  margin-bottom: 16px;
`

const DetailsCardTitle = styled(Typography)`
  margin-bottom: 16px;
`

const DetailsFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  margin-bottom: 16px;

  ${media.medium`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.small`
    grid-template-columns: auto;
  `}
`
const DetailsLongFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  margin-bottom: 16px;

  ${media.medium`
    grid-template-columns: repeat(1, 1fr);
  `}

  ${media.small`
    grid-template-columns: auto;
  `}
`

interface Props {
  fieldMaster: {
    [key: string]: FieldT
  }
  mode: 'add' | 'edit'
}

const parameterized = (str: string) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 -]/, '')
    .replace(/\s/g, '-')

const ContactForm: React.FC<Props> & { Skeleton: React.FC } = ({ fieldMaster, mode }) => {
  const { values, setFieldValue } = useFormikContext<any>()
  const { contactType } = values
  const titleFields = ['salutation', 'firstName', 'lastName']
  const keyFields = ['email', 'businessPhone', 'country', 'language']

  const contactFields = [
    'format',
    'company',
    'postcode',
    'place',
    'state',
    'position',
    'businessPhone',
    'mobilePhone',
    'privatePhone',
    'faxPhone',
  ]
  const financeFields = ['eori', 'vat']

  useEffect(() => {
    if (mode === 'add') {
      setFieldValue(
        'slug',
        [values.lastName ? parameterized(values.lastName) : null, values.firstName ? parameterized(values.firstName)[0] : null]
          .filter((v) => v !== null)
          .join('-')
      )
    }
  }, [values.lastName, values.firstName, setFieldValue, mode])

  const rolesRef = useRef(values.roles)
  useEffect(() => {
    if (values.email === '') {
      setFieldValue('email', null)
    }
    rolesRef.current = values.roles
  }, [values.roles, setFieldValue])

  return (
    <>
      <Hero backgroundColor='transparent'>
        <HeroContainer>
          {/* <ImageUpload /> */}
          <InputField of='imageSlug' field={fieldMaster.imageSlug} />
          <HeroDetailsCard>
            <HeroFieldsContainer>
              {titleFields.map((keyField) => (
                <InputField key={keyField} of={keyField} field={fieldMaster[keyField]} />
              ))}
            </HeroFieldsContainer>
            <HeroFieldsContainer>
              <InputField of={'slug'} field={fieldMaster['slug']} disabled={mode === 'edit'} />
              {keyFields.map((keyField) => (
                <InputField key={keyField} of={keyField} field={fieldMaster[keyField]} />
              ))}
            </HeroFieldsContainer>
          </HeroDetailsCard>
        </HeroContainer>
      </Hero>

      <ResponsiveGrid
        type='two-column-layout'
        left={[
          <DetailsCard key='contact-details'>
            <DetailsCardTitle variant='h6'>Contact Details</DetailsCardTitle>
            <DetailsFieldsContainer>
              {contactFields.slice(0, 2).map((keyField) => (
                <InputField key={keyField} of={keyField} field={fieldMaster[keyField]} />
              ))}
            </DetailsFieldsContainer>
            <DetailsLongFieldsContainer>
              <InputField of='address' field={fieldMaster['address']} variant='large' />
            </DetailsLongFieldsContainer>
          </DetailsCard>,
        ]}
        right={[
          <>
            {contactType === 'external' && (
              <>
                <DetailsCard key='finance-details'>
                  <DetailsCardTitle variant='h6'>Finance Details</DetailsCardTitle>
                  <DetailsFieldsContainer>
                    {financeFields.map((keyField) => (
                      <InputField key={keyField} of={keyField} field={fieldMaster[keyField]} />
                    ))}
                  </DetailsFieldsContainer>
                </DetailsCard>
              </>
            )}
          </>,
        ]}
      />
    </>
  )
}

const FieldSkeleton = styled(Skeleton).attrs({ variant: 'rect', height: '50px' })``

ContactForm.Skeleton = () => (
  <>
    <Hero backgroundColor='transparent'>
      <HeroContainer>
        <Skeleton variant='circle' height='184px' width='184px' />
        <HeroDetailsCard>
          <HeroFieldsContainer>
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton />
          </HeroFieldsContainer>
          <HeroFieldsContainer>
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton />
          </HeroFieldsContainer>
          <HeroFieldsContainer>
            <FieldSkeleton />
          </HeroFieldsContainer>
        </HeroDetailsCard>
      </HeroContainer>
    </Hero>

    <ResponsiveGrid
      type='two-column-layout'
      left={[
        <DetailsCard key='contact-details'>
          <Skeleton>
            <DetailsCardTitle variant='h6'>....................</DetailsCardTitle>
          </Skeleton>
          <DetailsFieldsContainer>
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton />
          </DetailsFieldsContainer>
        </DetailsCard>,
      ]}
      right={[
        <DetailsCard key='finance-details'>
          <Skeleton>
            <DetailsCardTitle variant='h6'>.............................</DetailsCardTitle>
          </Skeleton>
          <DetailsFieldsContainer>
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton />
          </DetailsFieldsContainer>
        </DetailsCard>,
      ]}
    />
  </>
)

export default ContactForm
